import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Spin,
  Statistic,
  Tabs,
  DatePicker,
  Select,
  Table,
  Timeline,
  Input,
  Avatar,
  Breadcrumb,
  Descriptions,
  Divider,
  Form,
  theme,
  message,
  Modal,
  Popconfirm,
  Collapse,
  Badge,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  DollarOutlined,
  SendOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  EditOutlined,
  FormOutlined,
  PlusOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
  CloudOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import api, { authenticatedFetch } from "@/libs/api";
import useAddressFinder, { STATE_ABBREV_TO_FULL } from "@/libs/useAddressFinder";
import NmiChangeModal from "./NmiChangeModal.react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  MARKET_TZ_LABEL,
  marketToday,
  marketInterval,
  currentMarketInterval,
  formatMarketTime,
  formatLocalWithZone,
} from "@/utils/MarketTimeUtil";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text, Paragraph } = Typography;

const VPP_FCAS_LABELS = {
  0: "Contact Admin",
  1: "Never Online",
  2: "Version 1.2.6",
  3: "NO CT",
  4: "Lost Connection With Inverter",
  5: "Offline for a long time",
  6: "Can't OTA, need restart",
  7: "Need Upgrade",
  8: "Registered",
  9: "Ready For Registered",
  10: "NO CT & Lost Connection With Inverter",
};

const VPP_FCAS_COLORS = {
  0: "red",
  1: "red",
  2: "red",
  3: "orange",
  4: "orange",
  5: "orange",
  6: "gold",
  7: "gold",
  8: "blue",
  9: "green",
  10: "red",
};

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// ============================================================================
// LEFT SECTION: System Data & Charts
// ============================================================================

// Charts Tab Component
function ChartsTab({ systemSn, selectedDay, onDayChange }) {
  const { token } = theme.useToken();
  const [timeMode, setTimeMode] = useState("day"); // 'day', 'month', 'year'
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    if (!systemSn) return;
    const fetchData = async () => {
      setChartLoading(true);
      try {
        if (timeMode === "day") {
          const date = selectedDay.format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/chart-data?date=${date}`);
          if (res.data.success && res.data.data) {
            // Keep null values for gaps in chart (don't convert to 0)
            // The 288 points are one market day; label them on the market clock
            // (see MarketTimeUtil) so the axis reads 00:00–23:55 for every viewer.
            const points = res.data.data.map((p) => ({
              label: formatMarketTime(p.ts),
              pv: p.pv !== null ? Math.round(p.pv * 100) / 100 : null,
              load: p.load !== null ? Math.round(p.load * 100) / 100 : null,
              battery: p.pbat !== null ? Math.round(p.pbat * 100) / 100 : null,
              // Grid: pmeter is signed at the source, positive = import,
              // negative = export (both vendors — see realtimeDataService.js
              // "Sign conventions"). Passed through unflipped, the same way
              // the Realtime tab's gridKw does it.
              grid: p.pmeter !== null && p.pmeter !== undefined ? Math.round(p.pmeter * 100) / 100 : null,
              // SoC is a percentage, not kW — it is plotted on the right-hand
              soc: p.soc !== null && p.soc !== undefined ? Math.round(p.soc * 100) / 100 : null,
            }));
            setChartData(points);
          } else {
            setChartData([]);
          }
        } else if (timeMode === "month") {
          const startDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
          const endDate = selectedMonth.endOf("month").format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/energy?startDate=${startDate}&endDate=${endDate}`);
          if (res.data.success && res.data.data) {
            const data = res.data.data.map((row) => ({
              label: dayjs(row.date).format("D"),
              pv: row.pvKwh,
              load: row.loadKwh,
              charge: row.chargeKwh,
              discharge: row.dischargeKwh,
              // Grid energy arrives already split into two positive totals,
              // the same shape as charge/discharge — not a signed figure.
              gridImport: row.importKwh,
              gridExport: row.exportKwh,
            }));
            setChartData(data);
          } else {
            setChartData([]);
          }
        } else if (timeMode === "year") {
          const startDate = selectedYear.startOf("year").format("YYYY-MM-DD");
          const endDate = selectedYear.endOf("year").format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/energy?startDate=${startDate}&endDate=${endDate}`);
          if (res.data.success && res.data.data) {
            // Aggregate daily rows into monthly buckets
            const monthMap = {};
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            for (const row of res.data.data) {
              const m = dayjs(row.date).month();
              if (!monthMap[m]) monthMap[m] = { pv: 0, load: 0, charge: 0, discharge: 0, gridImport: 0, gridExport: 0 };
              monthMap[m].pv += row.pvKwh || 0;
              monthMap[m].load += row.loadKwh || 0;
              monthMap[m].charge += row.chargeKwh || 0;
              monthMap[m].discharge += row.dischargeKwh || 0;
              monthMap[m].gridImport += row.importKwh || 0;
              monthMap[m].gridExport += row.exportKwh || 0;
            }
            const data = monthNames.map((name, i) => ({
              label: name,
              pv: Math.round((monthMap[i]?.pv || 0) * 100) / 100,
              load: Math.round((monthMap[i]?.load || 0) * 100) / 100,
              charge: Math.round((monthMap[i]?.charge || 0) * 100) / 100,
              discharge: Math.round((monthMap[i]?.discharge || 0) * 100) / 100,
              gridImport: Math.round((monthMap[i]?.gridImport || 0) * 100) / 100,
              gridExport: Math.round((monthMap[i]?.gridExport || 0) * 100) / 100,
            }));
            setChartData(data);
          } else {
            setChartData([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch chart data:", err);
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchData();
  }, [systemSn, timeMode, selectedDay, selectedMonth, selectedYear]);

  // Summary stats for month/year modes
  const totalPV = chartData.reduce((sum, d) => sum + (d.pv || 0), 0);
  const totalLoad = chartData.reduce((sum, d) => sum + (d.load || 0), 0);
  const totalCharge = chartData.reduce((sum, d) => sum + (d.charge || 0), 0);
  const totalDischarge = chartData.reduce((sum, d) => sum + (d.discharge || 0), 0);

  const getDatePicker = () => {
    switch (timeMode) {
      case "day":
        return <DatePicker value={selectedDay} onChange={onDayChange} allowClear={false} />;
      case "month":
        return <DatePicker picker="month" value={selectedMonth} onChange={setSelectedMonth} allowClear={false} />;
      case "year":
        return <DatePicker picker="year" value={selectedYear} onChange={setSelectedYear} allowClear={false} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, gap: 8 }}>
        <Select
          value={timeMode}
          onChange={setTimeMode}
          style={{ width: 100 }}
          options={[
            { value: "day", label: "Day" },
            { value: "month", label: "Month" },
            { value: "year", label: "Year" },
          ]}
        />
        {getDatePicker()}
      </div>

      {/* Summary Stats for Month/Year Mode */}
      {(timeMode === "month" || timeMode === "year") && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <div style={{ textAlign: "center", background: "rgba(250, 173, 20, 0.08)", padding: 12, borderRadius: 8, height: 72, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total PV</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#faad14" }}>{(Math.round(totalPV * 100) / 100).toFixed(2)} kWh</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center", background: "rgba(84, 162, 204, 0.08)", padding: 12, borderRadius: 8, height: 72, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total Load</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: token.colorPrimary }}>{(Math.round(totalLoad * 100) / 100).toFixed(2)} kWh</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center", background: "rgba(5, 166, 119, 0.08)", padding: 12, borderRadius: 8, height: 72, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total Charge</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: token.colorSuccess }}>{(Math.round(totalCharge * 100) / 100).toFixed(2)} kWh</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center", background: "rgba(255, 105, 180, 0.08)", padding: 12, borderRadius: 8, height: 72, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total Discharge</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#ff69b4" }}>{(Math.round(totalDischarge * 100) / 100).toFixed(2)} kWh</div>
            </div>
          </Col>
        </Row>
      )}

      {/* Chart — day: 280px, month/year: 240px (summary stats take ~100px above) */}
      <Spin spinning={chartLoading}>
      <div style={{ height: timeMode === "day" ? 280 : 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          {timeMode === "day" ? (
            <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#faad14" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#faad14" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="loadGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={23}
                tickFormatter={(value) => {
                  const hour = parseInt(value.split(":")[0], 10);
                  return hour % 2 === 0 ? value : "";
                }}
              />
              {/* Power series (kW) on the left axis. SoC is a percentage on a
                  completely different scale (0-100), so it gets its own right
                  axis — sharing one axis would flatten the kW series. */}
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
              />
              <RechartsTooltip
                formatter={(value, name, entry) => {
                  if (value === null || value === undefined) return ["—", name];
                  // Key off dataKey, not the display name — renaming the series
                  // must not silently start labelling a percentage as kW.
                  const isPct = entry?.dataKey === "soc";
                  return [`${value}${isPct ? "%" : " kW"}`, name];
                }}
                labelFormatter={(label) => `${label} ${MARKET_TZ_LABEL}`}
              />
              <Legend verticalAlign="bottom" height={36} />
              <Area yAxisId="left" type="monotone" dataKey="pv" name="PV (kW)" stroke="#faad14" fill="url(#pvGrad)" connectNulls={false} />
              <Area yAxisId="left" type="monotone" dataKey="load" name="Load (kW)" stroke={token.colorPrimary} fill="url(#loadGrad)" connectNulls={false} />
              <Area yAxisId="left" type="monotone" dataKey="battery" name="Battery (kW)" stroke="#52c41a" fill="none" connectNulls={false} />
              {/* Grid: positive = import, negative = export. Same cyan as the
                  "Grid" card on the Realtime tab. */}
              <Area yAxisId="left" type="monotone" dataKey="grid" name="Grid (kW)" stroke="#13c2c2" fill="none" connectNulls={false} />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="soc"
                name="SoC (%)"
                stroke="#722ed1"
                fill="none"
                strokeDasharray="4 2"
                dot={false}
                connectNulls={false}
              />
            </AreaChart>
          ) : (
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Legend verticalAlign="bottom" height={36} />
              <Bar dataKey="pv" name="PV (kWh)" fill="#faad14" />
              <Bar dataKey="load" name="Load (kWh)" fill={token.colorPrimary} />
              <Bar dataKey="charge" name="Charge (kWh)" fill={token.colorSuccess} />
              <Bar dataKey="discharge" name="Discharge (kWh)" fill="#ff69b4" />
              {/* Grid energy is reported as two separate positive totals, so it
                  gets two bars rather than one signed bar. */}
              <Bar dataKey="gridImport" name="Import (kWh)" fill="#13c2c2" />
              <Bar dataKey="gridExport" name="Export (kWh)" fill="#95de64" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      </Spin>
    </div>
  );
}

// Realtime Data Tab Component
// Data source: Cassandra powow_np_system_latest (realtime engine output)
function RealtimeDataTab({ systemSn }) {
  const { token } = theme.useToken();
  const [realtimeData, setRealtimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!systemSn) return;

    const fetchLatest = async () => {
      try {
        // Fetch from powow_np_system_latest via new realtime endpoint
        const res = await api.get(`/api/systems/${systemSn}/realtime`);
        if (res.data.success && res.data.data) {
          const latest = res.data.data;
          // Format updated_at_ts for display
          const updatedAt = latest.updatedAtTs
            ? formatLocalWithZone(latest.updatedAtTs)
            : "-";

          setRealtimeData({
            pvPower: (Math.abs(latest.pv || 0) * 1000).toFixed(0),
            loadPower: (Math.abs(latest.load || 0) * 1000).toFixed(0),
            batteryPower: ((latest.pbat || 0) * 1000).toFixed(0),
            gridPower: ((latest.pmeter || 0) * 1000).toFixed(0),
            batterySoc: Math.round(latest.soc || 0),
            pvKw: Math.abs(latest.pv || 0).toFixed(2),
            loadKw: Math.abs(latest.load || 0).toFixed(2),
            batteryKw: (latest.pbat || 0).toFixed(2),
            gridKw: (latest.pmeter || 0).toFixed(2),
            headroomKw: (latest.headroom || 0).toFixed(2),
            footroomKw: (latest.footroom || 0).toFixed(2),
            online: latest.online,
            gridStatus: latest.online ? "Connected" : "Offline",
            lastUpdate: updatedAt,
            // Status info
            errorCode: latest.errorCode,
            errorMessage: latest.errorMessage,
            workMode: latest.workMode || "UNKNOWN",
            hasError: latest.hasError,
            hasWarning: latest.hasWarning,
            // Dispatch info
            dispatchActive: latest.dispatchActive,
            dispatchCommand: latest.dispatchCommand,
            dispatchCompliance: latest.dispatchCompliance,
          });
        } else {
          // No realtime data available
          setRealtimeData({
            pvPower: "-", loadPower: "-", batteryPower: "-", gridPower: "-",
            batterySoc: "-", pvKw: "-", loadKw: "-", batteryKw: "-", gridKw: "-",
            headroomKw: "-", footroomKw: "-",
            gridStatus: "No Data", lastUpdate: "-",
            online: false, workMode: "-",
          });
        }
      } catch (err) {
        console.error("Failed to fetch realtime data:", err);
        setRealtimeData({
          pvPower: "-", loadPower: "-", batteryPower: "-", gridPower: "-",
          batterySoc: "-", pvKw: "-", loadKw: "-", batteryKw: "-", gridKw: "-",
          headroomKw: "-", footroomKw: "-",
          gridStatus: "Error", lastUpdate: "-",
          online: false, workMode: "-",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLatest();
    // Refresh every 30 seconds
    const interval = setInterval(fetchLatest, 30000);
    return () => clearInterval(interval);
  }, [systemSn]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>;
  }

  if (!realtimeData) return null;

  const MetricCard = ({ label, value, unit, color, status }) => (
    <div
      style={{
        flex: 1,
        background: status
          ? status === "Normal" || status === "Connected"
            ? "rgba(5, 166, 119, 0.08)"
            : "rgba(255, 77, 79, 0.08)"
          : "rgba(0, 0, 0, 0.02)",
        padding: "8px 6px",
        borderRadius: 6,
        textAlign: "center",
        border: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 2 }}>
        {label}
      </Text>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: status
            ? status === "Normal" || status === "Connected"
              ? token.colorSuccess
              : token.colorError
            : color || token.colorText,
        }}
      >
        {value}
        {unit && <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 1 }}>{unit}</span>}
      </div>
    </div>
  );

  const MetricRow = ({ children }) => (
    <div style={{ display: "flex", gap: 8 }}>
      {children}
    </div>
  );

  return (
    <div>
      {/* Row 1: Power (W) */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 11, color: token.colorTextSecondary, display: "block", marginBottom: 6 }}>
          Power (Watts)
        </Text>
        <MetricRow>
          <MetricCard label="PV" value={realtimeData.pvPower} unit="W" color="#faad14" />
          <MetricCard label="Load" value={realtimeData.loadPower} unit="W" color={token.colorPrimary} />
          <MetricCard label="Battery" value={realtimeData.batteryPower} unit="W" color="#52c41a" />
          <MetricCard label="Grid" value={realtimeData.gridPower} unit="W" color="#13c2c2" />
          <MetricCard label="SOC" value={realtimeData.batterySoc} unit="%" color={token.colorSuccess} />
        </MetricRow>
      </div>

      {/* Row 2: Capacity */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 11, color: token.colorTextSecondary, display: "block", marginBottom: 6 }}>
          Capacity (kW)
        </Text>
        <MetricRow>
          <MetricCard label="Headroom" value={realtimeData.headroomKw} unit="kW" color={token.colorSuccess} />
          <MetricCard label="Footroom" value={realtimeData.footroomKw} unit="kW" color={token.colorWarning} />
        </MetricRow>
      </div>

      {/* Row 3: System Status */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 11, color: token.colorTextSecondary, display: "block", marginBottom: 6 }}>
          System Status
        </Text>
        <MetricRow>
          <MetricCard
            label="Status"
            value={realtimeData.online ? "Online" : "Offline"}
            status={realtimeData.online ? "Connected" : "Offline"}
          />
          <MetricCard label="Work Mode" value={realtimeData.workMode} color={token.colorTextSecondary} />
          {realtimeData.hasError && (
            <MetricCard label="Error" value={realtimeData.errorCode || "Yes"} status="Error" />
          )}
        </MetricRow>
      </div>

      {/* Row 4: Last Update */}
      <div>
        <Text strong style={{ fontSize: 11, color: token.colorTextSecondary, display: "block", marginBottom: 6 }}>
          Data Timestamp
        </Text>
        <MetricRow>
          <MetricCard label="Last Updated" value={realtimeData.lastUpdate} color={token.colorTextSecondary} />
          {realtimeData.dispatchActive && (
            <MetricCard
              label="Dispatch"
              value={`${realtimeData.dispatchCommand} (${realtimeData.dispatchCompliance}%)`}
              color="#f5a623"
            />
          )}
        </MetricRow>
      </div>
    </div>
  );
}

// Online Status Tab Component
function OnlineStatusTab({ systemSn }) {
  const { token } = theme.useToken();
  const [timeMode, setTimeMode] = useState("day");
  const [selectedDay, setSelectedDay] = useState(marketToday());
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [onlineData, setOnlineData] = useState([]);
  const [stats, setStats] = useState({ totalIntervals: 0, onlineIntervals: 0, onlineRate: 0 });

  useEffect(() => {
    if (!systemSn) return;
    const fetchOnline = async () => {
      try {
        if (timeMode === "day") {
          const date = selectedDay.format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/chart-data?date=${date}`);
          const points = (res.data.success && res.data.data) ? res.data.data : [];
          // Build 288-interval grid; mark as online only if data point has real values (not all null).
          // Bucket by position in the market day, and count "so far" on the market clock:
          // bucketing by the viewer's clock left 00:00–00:55 empty for AEDT viewers and put
          // the previous evening's readings on today's bars for viewers in India (POW-195).
          const onlineSet = new Set(
            points
              .filter((p) => p.pv != null || p.load != null || p.pbat != null)
              .map((p) => marketInterval(p.ts, date))
          );
          const isToday = date === marketToday().format("YYYY-MM-DD");
          const currentInterval = isToday ? currentMarketInterval() : 287;
          const data = [];
          let onlineCount = 0;
          for (let i = 0; i <= currentInterval; i++) {
            const hour = Math.floor(i / 12);
            const minute = (i % 12) * 5;
            const isOnline = onlineSet.has(i) ? 1 : 0;
            onlineCount += isOnline;
            data.push({
              interval: i,
              label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
              online: isOnline,
            });
          }
          const totalIntervals = currentInterval + 1;
          setOnlineData(data);
          setStats({
            totalIntervals,
            onlineIntervals: onlineCount,
            onlineRate: totalIntervals > 0 ? ((onlineCount / totalIntervals) * 100).toFixed(1) : "0.0",
          });
        } else if (timeMode === "month") {
          const startDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
          const endDate = selectedMonth.endOf("month").format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/settlement?startDate=${startDate}&endDate=${endDate}`);
          const rows = (res.data.success && res.data.data) ? res.data.data : [];
          const dateMap = {};
          for (const row of rows) dateMap[dayjs(row.date).format("D")] = row;
          const daysInMonth = selectedMonth.daysInMonth();
          const data = [];
          let totalOnline = 0;
          let totalIntervals = 0;
          for (let day = 1; day <= daysInMonth; day++) {
            const row = dateMap[String(day)];
            // If we have settlement data for a day, assume it was online
            const dailyOnline = row ? 288 : 0;
            totalOnline += dailyOnline;
            totalIntervals += 288;
            data.push({
              label: day.toString(),
              onlineIntervals: dailyOnline,
              onlineRate: row ? "100.0" : "0.0",
            });
          }
          setOnlineData(data);
          setStats({
            totalIntervals,
            onlineIntervals: totalOnline,
            onlineRate: totalIntervals > 0 ? ((totalOnline / totalIntervals) * 100).toFixed(1) : "0.0",
          });
        } else if (timeMode === "year") {
          const startDate = selectedYear.startOf("year").format("YYYY-MM-DD");
          const endDate = selectedYear.endOf("year").format("YYYY-MM-DD");
          const res = await api.get(`/api/systems/${systemSn}/settlement?startDate=${startDate}&endDate=${endDate}`);
          const rows = (res.data.success && res.data.data) ? res.data.data : [];
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const monthDays = {};
          for (const row of rows) {
            const m = dayjs(row.date).month();
            monthDays[m] = (monthDays[m] || 0) + 1;
          }
          const data = [];
          let totalOnline = 0;
          let totalIntervals = 0;
          monthNames.forEach((name, idx) => {
            const daysInMonth = dayjs().month(idx).daysInMonth();
            const monthlyIntervals = daysInMonth * 288;
            const daysWithData = monthDays[idx] || 0;
            const monthlyOnline = daysWithData * 288;
            totalOnline += monthlyOnline;
            totalIntervals += monthlyIntervals;
            data.push({
              label: name,
              onlineIntervals: monthlyOnline,
              totalIntervals: monthlyIntervals,
              onlineRate: monthlyIntervals > 0 ? ((monthlyOnline / monthlyIntervals) * 100).toFixed(1) : "0.0",
            });
          });
          setOnlineData(data);
          setStats({
            totalIntervals,
            onlineIntervals: totalOnline,
            onlineRate: totalIntervals > 0 ? ((totalOnline / totalIntervals) * 100).toFixed(1) : "0.0",
          });
        }
      } catch (err) {
        console.error("Failed to fetch online data:", err);
      }
    };
    fetchOnline();
  }, [systemSn, timeMode, selectedDay, selectedMonth, selectedYear]);

  const getDatePicker = () => {
    switch (timeMode) {
      case "day":
        return <DatePicker value={selectedDay} onChange={setSelectedDay} allowClear={false} />;
      case "month":
        return <DatePicker picker="month" value={selectedMonth} onChange={setSelectedMonth} allowClear={false} />;
      case "year":
        return <DatePicker picker="year" value={selectedYear} onChange={setSelectedYear} allowClear={false} />;
      default:
        return null;
    }
  };

  const getOnlineRateColor = (rate) => {
    const r = parseFloat(rate);
    if (r >= 98) return token.colorSuccess;
    if (r >= 90) return token.colorWarning;
    return token.colorError;
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, gap: 8 }}>
        <Select
          value={timeMode}
          onChange={setTimeMode}
          style={{ width: 100 }}
          options={[
            { value: "day", label: "Day" },
            { value: "month", label: "Month" },
            { value: "year", label: "Year" },
          ]}
        />
        {getDatePicker()}
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <div style={{ textAlign: "center", background: "rgba(84, 162, 204, 0.08)", padding: 12, borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Total Intervals</Text>
            <div style={{ fontSize: 18, fontWeight: 600, color: token.colorPrimary }}>{stats.totalIntervals.toLocaleString()}</div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: "center", background: "rgba(5, 166, 119, 0.08)", padding: 12, borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Online Intervals</Text>
            <div style={{ fontSize: 18, fontWeight: 600, color: token.colorSuccess }}>{stats.onlineIntervals.toLocaleString()}</div>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ textAlign: "center", background: `rgba(${parseFloat(stats.onlineRate) >= 98 ? '5, 166, 119' : parseFloat(stats.onlineRate) >= 90 ? '250, 173, 20' : '255, 77, 79'}, 0.08)`, padding: 12, borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Online Rate</Text>
            <div style={{ fontSize: 18, fontWeight: 600, color: getOnlineRateColor(stats.onlineRate) }}>{stats.onlineRate}%</div>
          </div>
        </Col>
      </Row>

      {/* Chart */}
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          {timeMode === "day" ? (
            <BarChart data={onlineData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 9 }}
                interval={23} // Show every 2 hours
              />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 1]} ticks={[0, 1]} />
              <RechartsTooltip
                formatter={(value, name) => [value === 1 ? "Online" : "Offline", "Status"]}
                labelFormatter={(label) => `Time: ${label} ${MARKET_TZ_LABEL}`}
              />
              <Bar
                dataKey="online"
                name="Online"
                fill={token.colorSuccess}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          ) : (
            <BarChart data={onlineData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={[85, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <RechartsTooltip
                formatter={(value, name) => [`${value}%`, "Online Rate"]}
              />
              <Bar
                dataKey="onlineRate"
                name="Online Rate"
                fill={token.colorSuccess}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// System Config Tab Component — simple text layout
function SystemConfigTab({ systemConfig }) {
  const config = systemConfig || {};

  return (
    <Descriptions column={2} size="small" labelStyle={{ width: 110 }}>
      <Descriptions.Item label="PV Size">{config.pvSize || "-"}</Descriptions.Item>
      <Descriptions.Item label="Inverter Size">{config.inverterSize || "-"}</Descriptions.Item>
      <Descriptions.Item label="Inverter Model">{config.inverterModel || "-"}</Descriptions.Item>
      <Descriptions.Item label="Battery Size">{config.batterySize || "-"}</Descriptions.Item>
      <Descriptions.Item label="Battery Model">{config.batteryModel || "-"}</Descriptions.Item>
      <Descriptions.Item label="Meter">{config.meterModel || "-"}</Descriptions.Item>
      {/* POW-190: onboard_date is np_system.onboard_date — a property of the
          SYSTEM, not the customer. It used to sit on the Customer card, which
          implied it belonged to the customer record shared across their
          systems. */}
      <Descriptions.Item label="Onboarded">{config.onboardDate || "-"}</Descriptions.Item>
      {(config.extCrm || config.extCrmId) && <Descriptions.Item label="Ext CRM">{config.extCrm || "-"} / {config.extCrmId || "-"}</Descriptions.Item>}
    </Descriptions>
  );
}

// Control Group Tab Component
function ControlGroupTab({ groupName, groupId, stats, region, dnsp }) {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  if (!groupId) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: token.colorTextSecondary }}>
        No control group assigned
      </div>
    );
  }

  const systemCount = stats?.system_count || 0;
  const totalKwh = stats?.total_kwh ? parseFloat(stats.total_kwh) : 0;
  const totalKw = stats?.total_kw ? parseFloat(stats.total_kw) : 0;

  return (
    <div style={{
      background: `linear-gradient(135deg, ${token.colorPrimary}15 0%, ${token.colorPrimary}05 100%)`,
      borderRadius: 12,
      padding: 20,
      border: `1px solid ${token.colorPrimary}30`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Control Group</Text>
          <Title level={4} style={{ margin: "4px 0 8px", color: token.colorPrimary }}>{groupName}</Title>
          <Space>
            <Tag color="blue">{groupId}</Tag>
            {region && <Tag color="cyan">{region}</Tag>}
            {dnsp && <Tag color="geekblue">{dnsp}</Tag>}
          </Space>
        </div>
        <Button
          type="primary"
          onClick={() => navigate(`/group-management/groups/${groupId}`)}
          style={{ borderRadius: 2 }}
        >
          View Group
        </Button>
      </div>
      <Row gutter={[12, 12]}>
        <Col span={8}>
          <div style={{ background: "rgba(255,255,255,0.6)", padding: 12, borderRadius: 8, textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Systems</Text>
            <Text strong style={{ fontSize: 18, color: token.colorPrimary }}>{systemCount}</Text>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ background: "rgba(255,255,255,0.6)", padding: 12, borderRadius: 8, textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Inverter</Text>
            {(() => { const f = formatPower(totalKw || 0, "kW"); return (
            <Text strong style={{ fontSize: 18, color: "#f5a623" }}>{f.value} {f.suffix}</Text>); })()}
          </div>
        </Col>
        <Col span={8}>
          <div style={{ background: "rgba(255,255,255,0.6)", padding: 12, borderRadius: 8, textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Battery</Text>
            {(() => { const f = formatPower(totalKwh || 0, "kWh"); return (
            <Text strong style={{ fontSize: 18, color: token.colorSuccess }}>{f.value} {f.suffix}</Text>); })()}
          </div>
        </Col>
      </Row>
    </div>
  );
}

// View Groups Tab Component (can observe and share revenue, but cannot control)
function ViewGroupsTab({ viewGroups }) {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const groups = viewGroups || [];

  const handleViewGroup = (groupId) => {
    navigate(`/group-management/view-groups/${groupId}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Text type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
        View Groups can observe system data and share revenue, but cannot control dispatch
      </Text>
      {groups.map((group) => (
        <div
          key={group.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderRadius: 8,
            border: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillQuaternary,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Text strong>{group.name}</Text>
            </div>
            <Space size={16}>
              <Text type="secondary" style={{ fontSize: 12 }}>{group.company}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{group.systemCount} systems</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>Since {group.joinedDate}</Text>
            </Space>
          </div>
          <Button
            type="link"
            onClick={() => handleViewGroup(group.id)}
            style={{ padding: 0 }}
          >
            View →
          </Button>
        </div>
      ))}
      {groups.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: token.colorTextSecondary }}>
          No view groups assigned
        </div>
      )}
    </div>
  );
}

// Combined System Info tab: Config + Control Group + View Groups
function SystemInfoTab({ systemConfig, groupName, groupId, viewGroups, controlGroupStats, controlGroupRegion, controlGroupDnsp }) {
  const { token } = theme.useToken();
  return (
    <div style={{ height: 380, overflowY: "auto" }}>
      <Text strong style={{ fontSize: 12, color: token.colorTextSecondary, display: "block", marginBottom: 8 }}>System Config</Text>
      <SystemConfigTab systemConfig={systemConfig} />
      <Divider style={{ margin: "12px 0" }} />
      <Text strong style={{ fontSize: 12, color: token.colorTextSecondary, display: "block", marginBottom: 8 }}>Control Group</Text>
      <ControlGroupTab groupName={groupName} groupId={groupId} stats={controlGroupStats} region={controlGroupRegion} dnsp={controlGroupDnsp} />
      {viewGroups && viewGroups.length > 0 && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <Text strong style={{ fontSize: 12, color: token.colorTextSecondary, display: "block", marginBottom: 8 }}>View Groups</Text>
          <ViewGroupsTab viewGroups={viewGroups} />
        </>
      )}
    </div>
  );
}

// Weather tab content (inline within SystemDataSection)
function WeatherTab({ systemSn, selectedDate }) {
  const { token } = theme.useToken();
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const dateStr = selectedDate ? selectedDate.format("YYYY-MM-DD") : null;

  useEffect(() => {
    if (!systemSn) return;
    const fetchWeather = async () => {
      setLoading(true);
      setError(false);
      try {
        const url = dateStr
          ? `/api/weather/system/${systemSn}?date=${dateStr}`
          : `/api/weather/system/${systemSn}`;
        const response = await authenticatedFetch(url);
        const result = await response.json();
        if (result.r === 0 && result.data) {
          setWeatherData(result.data);
        } else {
          setWeatherData(null);
        }
      } catch (err) {
        console.error("Failed to fetch weather data:", err);
        setError(true);
        setWeatherData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, [systemSn, dateStr]);

  const cluster = weatherData?.cluster;
  const daily = weatherData?.daily_summary;
  const weather = weatherData?.weather || [];

  // Build chart data: same HH:mm format as power chart
  const chartData = (() => {
    if (weather.length < 2) return [];
    const sorted = [...weather].sort((a, b) => a.timestamp - b.timestamp);
    let lastHistoryIdx = -1;
    sorted.forEach((pt, i) => { if (pt.forecast_history === "history") lastHistoryIdx = i; });

    const data = sorted.map((pt) => {
      const isH = pt.forecast_history === "history";
      // Weather timestamps are AEST stored as UTC in Cassandra — display as UTC directly
      const label = pt.timestamp != null
        ? dayjs.utc(pt.timestamp * 1000).format("HH:mm")
        : "--:--";
      return {
        timeLabel: label,
        temp_h: isH ? pt.temp_c : null,
        temp_f: !isH ? pt.temp_c : null,
        short_h: isH ? pt.short_rad : null,
        short_f: !isH ? pt.short_rad : null,
        diff_h: isH ? pt.diff_rad : null,
        diff_f: !isH ? pt.diff_rad : null,
        cloud_h: isH ? pt.cloud_pct : null,
        cloud_f: !isH ? pt.cloud_pct : null,
      };
    });

    if (lastHistoryIdx >= 0 && lastHistoryIdx < sorted.length - 1) {
      const bp = sorted[lastHistoryIdx];
      data[lastHistoryIdx + 1] = {
        ...data[lastHistoryIdx + 1],
        temp_h: bp.temp_c, short_h: bp.short_rad, diff_h: bp.diff_rad, cloud_h: bp.cloud_pct,
      };
    }
    return data;
  })();

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;
  if (error) return <div style={{ textAlign: "center", padding: 40 }}><Text type="secondary">Failed to load weather data</Text></div>;
  if (!cluster) return <div style={{ textAlign: "center", padding: 40 }}><Text type="secondary">No weather station mapped</Text></div>;

  return (
    <div style={{ height: 380 }}>
      {/* Station info + daily summary */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Tag color="blue"><EnvironmentOutlined style={{ marginRight: 4 }} />{cluster.cluster_name} ({cluster.distance_km != null ? `${cluster.distance_km} km` : ""})</Tag>
        {daily && <Tag>{daily.condition}</Tag>}
        {daily && <Text type="secondary" style={{ fontSize: 11 }}>
          {daily.mintemp_c}°–{daily.maxtemp_c}°C | Humidity {daily.avghumidity}% | UV {daily.uv}
        </Text>}
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div>
          <div style={{ marginBottom: 4, display: "flex", gap: 12, fontSize: 10, color: token.colorTextSecondary }}>
            <span><span style={{ display: "inline-block", width: 12, borderBottom: "2px solid #ff7875", marginRight: 3 }} />Temp °C</span>
            <span><span style={{ display: "inline-block", width: 12, borderBottom: "2px solid #faad14", marginRight: 3 }} />GHI W/m²</span>
            <span><span style={{ display: "inline-block", width: 12, borderBottom: "2px solid #b37feb", marginRight: 3 }} />Diff W/m²</span>
            <span><span style={{ display: "inline-block", width: 12, borderBottom: "2px solid #69b1ff", marginRight: 3 }} />Cloud %</span>
            <span><span style={{ display: "inline-block", width: 12, borderBottom: "2px dashed #999", marginRight: 3 }} />forecast</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis
                dataKey="timeLabel"
                tick={{ fontSize: 10 }}
                interval={7}
                stroke={token.colorTextQuaternary}
              />
              <YAxis yAxisId="temp" tick={{ fontSize: 10 }} stroke={token.colorTextQuaternary} />
              <YAxis yAxisId="rad" orientation="right" tick={{ fontSize: 10 }} stroke={token.colorTextQuaternary} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${token.colorBorderSecondary}` }}
                labelFormatter={(l) => `Time: ${l}`}
                formatter={(val, name) => {
                  if (val == null) return [null, null];
                  const map = { temp_h: "Temp", temp_f: "Temp (fc)", short_h: "GHI", short_f: "GHI (fc)", diff_h: "Diff", diff_f: "Diff (fc)", cloud_h: "Cloud", cloud_f: "Cloud (fc)" };
                  const unit = name.startsWith("temp") ? "°C" : name.startsWith("cloud") ? "%" : " W/m²";
                  return [`${Number(val).toFixed(1)}${unit}`, map[name] || name];
                }}
              />
              <Line yAxisId="temp" type="monotone" dataKey="temp_h" stroke="#ff7875" strokeWidth={2} dot={false} connectNulls={false} name="temp_h" />
              <Line yAxisId="temp" type="monotone" dataKey="temp_f" stroke="#ff7875" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls={false} name="temp_f" />
              <Line yAxisId="temp" type="monotone" dataKey="cloud_h" stroke="#69b1ff" strokeWidth={1.5} dot={false} connectNulls={false} name="cloud_h" />
              <Line yAxisId="temp" type="monotone" dataKey="cloud_f" stroke="#69b1ff" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls={false} name="cloud_f" />
              <Line yAxisId="rad" type="monotone" dataKey="short_h" stroke="#faad14" strokeWidth={2} dot={false} connectNulls={false} name="short_h" />
              <Line yAxisId="rad" type="monotone" dataKey="short_f" stroke="#faad14" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls={false} name="short_f" />
              <Line yAxisId="rad" type="monotone" dataKey="diff_h" stroke="#b37feb" strokeWidth={1.5} dot={false} connectNulls={false} name="diff_h" />
              <Line yAxisId="rad" type="monotone" dataKey="diff_f" stroke="#b37feb" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls={false} name="diff_f" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary">No weather readings for this date</Text>
        </div>
      )}
    </div>
  );
}

function SystemDataSection({ systemSn, systemConfig, groupName, groupId, viewGroups, controlGroupStats, controlGroupRegion, controlGroupDnsp, selectedDay, onDayChange }) {
  const { token } = theme.useToken();
  const [activeTab, setActiveTab] = useState("charts");

  const tabItems = [
    {
      key: "charts",
      label: "Charts",
      children: <div style={{ minHeight: 380 }}><ChartsTab systemSn={systemSn} selectedDay={selectedDay} onDayChange={onDayChange} /></div>,
    },
    {
      key: "realtime",
      label: "Realtime",
      children: <div style={{ height: 380 }}><RealtimeDataTab systemSn={systemSn} /></div>,
    },
    {
      key: "online",
      label: "Online",
      children: <div style={{ height: 380 }}><OnlineStatusTab systemSn={systemSn} /></div>,
    },
    {
      key: "weather",
      label: <span><CloudOutlined style={{ marginRight: 4 }} />Weather</span>,
      children: <WeatherTab systemSn={systemSn} selectedDate={selectedDay} />,
    },
    {
      key: "info",
      label: "System Info",
      children: <SystemInfoTab systemConfig={systemConfig} groupName={groupName} groupId={groupId} viewGroups={viewGroups} controlGroupStats={controlGroupStats} controlGroupRegion={controlGroupRegion} controlGroupDnsp={controlGroupDnsp} />,
    },
  ];

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>System Details</Text>}
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" />
    </Card>
  );
}

// ============================================================================
// VPP POLICY VIEWER (used inside Customer Details -> VPP Policy tab)
// ============================================================================

function VppPolicyViewer({ policy, policyId, timezone }) {
  const { token } = theme.useToken();

  if (!policy || typeof policy !== "object") {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Text type="secondary">Invalid policy data</Text>
      </div>
    );
  }

  const constraints = policy.constraints || {};
  const layerControl = policy.layer_control || {};
  const layers = policy.layers || [];

  // SOC constraint bar helper
  const SocBar = ({ min, max, label }) => {
    const minVal = min != null ? Number(min) : 0;
    const maxVal = max != null ? Number(max) : 100;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <Text style={{ fontSize: 12 }}>{label}</Text>
          <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
            {minVal}% - {maxVal}%
          </Text>
        </div>
        <div
          style={{
            position: "relative",
            height: 18,
            background: token.colorFillQuaternary,
            borderRadius: 9,
            overflow: "hidden",
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {/* Allowed range fill */}
          <div
            style={{
              position: "absolute",
              left: `${minVal}%`,
              width: `${maxVal - minVal}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${token.colorWarning}88, ${token.colorSuccess}88)`,
              borderRadius: 9,
            }}
          />
          {/* Min marker */}
          <div
            style={{
              position: "absolute",
              left: `${minVal}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: token.colorWarning,
            }}
          />
          {/* Max marker */}
          <div
            style={{
              position: "absolute",
              left: `${maxVal}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: token.colorSuccess,
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxHeight: 400, overflowY: "auto" }}>
      {/* Policy summary header */}
      {policyId && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Policy ID: <Text code style={{ fontSize: 11 }}>{policyId}</Text>
          </Text>
          {timezone && (
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 12 }}>
              TZ: {timezone}
            </Text>
          )}
        </div>
      )}

      {/* SOC Constraints */}
      {constraints.soc && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            SOC Constraints
          </Text>
          <SocBar
            min={constraints.soc.min}
            max={constraints.soc.max}
            label="Allowed SOC Range"
          />
        </div>
      )}

      {/* Other constraints */}
      {(constraints.max_charge_kw != null || constraints.max_discharge_kw != null) && (
        <div style={{ marginBottom: 16 }}>
          <Row gutter={8}>
            {constraints.max_charge_kw != null && (
              <Col span={12}>
                <div style={{ background: "rgba(5, 166, 119, 0.08)", padding: 8, borderRadius: 6, textAlign: "center" }}>
                  <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Max Charge</Text>
                  <Text strong style={{ fontSize: 14, color: token.colorSuccess }}>{constraints.max_charge_kw} kW</Text>
                </div>
              </Col>
            )}
            {constraints.max_discharge_kw != null && (
              <Col span={12}>
                <div style={{ background: "rgba(245, 166, 35, 0.08)", padding: 8, borderRadius: 6, textAlign: "center" }}>
                  <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Max Discharge</Text>
                  <Text strong style={{ fontSize: 14, color: "#f5a623" }}>{constraints.max_discharge_kw} kW</Text>
                </div>
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Layer summary */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
          Layers ({layers.length})
        </Text>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {layers.map((layer, idx) => {
            const isEnabled = layer.enabled !== false;
            return (
              <Tag
                key={layer.name || idx}
                color={isEnabled ? "blue" : "default"}
                style={{ marginRight: 0 }}
              >
                <Badge
                  status={isEnabled ? "processing" : "default"}
                  style={{ marginRight: 4 }}
                />
                {layer.name || `Layer ${idx + 1}`}
              </Tag>
            );
          })}
          {layers.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>No layers defined</Text>
          )}
        </div>
      </div>

      {/* Layer control summary */}
      {layerControl && Object.keys(layerControl).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={1} style={{ marginBottom: 0 }}>
            {layerControl.mode && (
              <Descriptions.Item label="Mode">
                <Tag color="purple">{layerControl.mode}</Tag>
              </Descriptions.Item>
            )}
            {layerControl.priority && (
              <Descriptions.Item label="Priority">
                {layerControl.priority}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      )}

      {/* Full policy JSON accordion */}
      <Collapse
        size="small"
        items={[
          {
            key: "fullPolicy",
            label: (
              <Space>
                <SafetyCertificateOutlined />
                <Text style={{ fontSize: 12 }}>View Full Policy JSON</Text>
              </Space>
            ),
            children: (
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  background: token.colorFillQuaternary,
                  borderRadius: 6,
                  fontSize: 11,
                  lineHeight: 1.5,
                  maxHeight: 300,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {JSON.stringify(policy, null, 2)}
              </pre>
            ),
          },
        ]}
      />
    </div>
  );
}

// ============================================================================
// ============================================================================
// CUSTOMER DETAILS COMPACT (top of page)
// ============================================================================

/**
 * POW-187 — the system's OWN site address.
 *
 * Separate from the Customer card on purpose. np_customer holds one address
 * shared by every system that customer owns, so editing it there rewrote all of
 * them. This card reads and writes np_system for THIS system only.
 *
 * POW-190: address lookup calls AddressFinder DIRECTLY from the browser via
 * useAddressFinder, the same way onboarding always has. POW-187 routed it
 * through the backend proxy (/api/address/*), which calls AddressFinder
 * server-side — the mode our plan rejects with "Direct API calls are not
 * permitted on your current plan". In production every search came back empty
 * and users saved free text with no coordinates.
 *
 * Coordinates come from the AddressFinder result the user picked. Free-typed
 * addresses save the text and CLEAR lat/long rather than keeping the previous
 * site's pin — a wrong pin is worse than a missing one.
 */
function SiteAddressCard({ systemId, siteAddress, onSiteAddressUpdated }) {
  const { token } = theme.useToken();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState(null);
  const [typed, setTyped] = useState("");
  const { options, searching, search, resolve, clear } = useAddressFinder();

  const handleEdit = () => {
    setTyped(siteAddress?.display || "");
    setPicked(null);
    clear();
    setIsEditing(true);
  };

  // Resolve the chosen suggestion to its structured parts + coordinates.
  const handlePick = async (_value, option) => {
    setTyped(option?.label || "");
    const d = await resolve(option?.data?.id);
    if (!d) {
      // No coordinates resolved — fall through to the free-text path on save,
      // which clears lat/long rather than keeping a stale pin.
      message.warning("Could not resolve coordinates for that address");
      return;
    }
    setPicked({
      street: d.address_line_1 || "",
      suburb: d.locality_name || "",
      // AddressFinder returns abbreviations; np_system stores full names,
      // matching what onboarding writes.
      state: STATE_ABBREV_TO_FULL[d.state_territory] || d.state_territory || "",
      postcode: d.postcode || "",
      addressline: d.full_address || option?.label || "",
      latitude: d.latitude,
      longitude: d.longitude,
    });
  };

  const handleSave = async () => {
    const text = (typed || "").trim();
    if (!text) { message.error("Address is required"); return; }

    // A picked suggestion carries coordinates. Free text does not — save the
    // text and clear the old coordinates rather than leaving a stale pin.
    const body = picked && picked.addressline === text
      ? picked
      : { street: text, suburb: "", state: "", postcode: "", addressline: text, latitude: null, longitude: null };

    setSaving(true);
    try {
      // POW-192: address the system by ID, never by serial number. An SN is not
      // unique in np_system (111 SNs have >1 active row; 61 of those span more
      // than one company), so a save keyed on SN could rewrite a different
      // system — possibly another partner's. `system.id` is the row this page
      // actually loaded, so the record written is always the one on screen.
      const resp = await authenticatedFetch(`/api/systems/${encodeURIComponent(systemId)}/address`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await resp.json();
      if (result.success) {
        message.success("Site address updated for this system only");
        setIsEditing(false);
        if (onSiteAddressUpdated) onSiteAddressUpdated(result.data);
      } else {
        message.error(result.message || "Failed to update site address");
      }
    } catch (err) {
      message.error("Failed to update site address");
    } finally {
      setSaving(false);
    }
  };

  const hasCoords = siteAddress?.latitude != null && siteAddress?.longitude != null;

  return (
    <Card
      style={{ borderRadius: 12, marginBottom: 16 }}
      title={
        <Space>
          <EnvironmentOutlined style={{ color: token.colorPrimary }} />
          <Text strong style={{ fontSize: 16 }}>Site Address</Text>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: "12px 16px" },
      }}
      extra={!isEditing && <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>Edit</Button>}
    >
      {isEditing ? (
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <AutoComplete
            style={{ width: "100%" }}
            value={typed}
            options={options}
            onSearch={search}
            onChange={(v) => { setTyped(v); setPicked(null); }}
            onSelect={handlePick}
            placeholder="Start typing the site address…"
            notFoundContent={searching ? "Searching…" : null}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Applies to this system only. The customer record is not changed.
            {!picked && typed ? " Coordinates will be cleared unless you pick a suggestion." : ""}
          </Text>
          <Space>
            <Button size="small" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button size="small" type="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Space>
        </Space>
      ) : (
        <Row gutter={[24, 8]} align="middle">
          <Col flex="auto">
            <Text type="secondary" style={{ fontSize: 11 }}>Address</Text>
            <div><Text strong>{siteAddress?.display || "-"}</Text></div>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>Coordinates</Text>
            <div>
              <Text type={hasCoords ? undefined : "secondary"}>
                {hasCoords ? `${siteAddress.latitude}, ${siteAddress.longitude}` : "not geocoded"}
              </Text>
            </div>
          </Col>
        </Row>
      )}
    </Card>
  );
}

function CustomerDetailsCompact({ customer, onCustomerUpdated }) {
  const { token } = theme.useToken();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customerForm] = Form.useForm();

  const handleEdit = () => {
    customerForm.setFieldsValue({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      // POW-190: no address field — the site address is edited in its own card,
      // and the read-only Contact Address below still shows np_customer.address.
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!customer.id) { message.error("No customer linked"); return; }
    try {
      const values = await customerForm.validateFields();
      setSaving(true);
      const resp = await authenticatedFetch(`/api/customer/${customer.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const result = await resp.json();
      if (result.r === 0) {
        message.success("Customer details updated");
        setIsEditing(false);
        if (onCustomerUpdated) onCustomerUpdated(result.d);
      } else {
        message.error(result.error || "Failed to update customer");
      }
    } catch (error) {
      console.error("Error updating customer:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, marginBottom: 16 }}
      title={
        <Space>
          <UserOutlined style={{ color: token.colorPrimary }} />
          <Text strong style={{ fontSize: 16 }}>Customer</Text>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: "12px 16px" },
      }}
      extra={!isEditing && <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>Edit</Button>}
    >
      {/* POW-187: one np_customer row can be linked to several systems, and it
          holds a single address. Editing anything here rewrites what every
          sibling system's page shows, so say so plainly — before and during
          editing, not as a post-hoc confirmation. */}
      {/* POW-190: only while editing. Shown permanently it read as a warning
          about the SYSTEM, when it is really a caution about saving a change to
          the shared customer record. */}
      {isEditing && customer.systemCount > 1 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`This customer record is linked to ${customer.systemCount} systems — changes here affect all of them.`}
          description="The site address for this system is edited separately, in the Site Address card."
        />
      )}
      {isEditing ? (
        <Form form={customerForm} layout="inline" size="small" style={{ gap: 8, flexWrap: "wrap" }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
            <Input style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="phone" label="Phone" style={{ marginBottom: 8 }}>
            <Input style={{ width: 130 }} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: "email" }]} style={{ marginBottom: 8 }}>
            <Input style={{ width: 180 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Space>
              <Button size="small" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button size="small" type="primary" onClick={handleSave} loading={saving}>Save</Button>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <Row gutter={[24, 8]} align="middle">
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>Name</Text>
            <div><Text strong>{customer.name || "-"}</Text></div>
          </Col>
          <Col>
            {/* POW-187: explicitly the CONTACT address. The site address is
                per-system and rendered by SiteAddressCard. */}
            <Text type="secondary" style={{ fontSize: 11 }}>Contact Address</Text>
            <div><Text>{customer.address || "-"}</Text></div>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>Phone</Text>
            <div><Text>{customer.phone || "-"}</Text></div>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>Email</Text>
            <div><Text>{customer.email || "-"}</Text></div>
          </Col>
          {customer.totalRevenue != null && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>Total Revenue</Text>
              <div><Text strong style={{ color: token.colorSuccess }}>${customer.totalRevenue}</Text></div>
            </Col>
          )}
          {customer.monthRevenue != null && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>This Month</Text>
              <div><Text strong style={{ color: token.colorPrimary }}>${customer.monthRevenue}</Text></div>
            </Col>
          )}
        </Row>
      )}
    </Card>
  );
}

// ============================================================================
// STATEMENTS SECTION (standalone)
// ============================================================================

// ============================================================================
// CUSTOMER ACCOUNT TABS (Statements, Bank, VPP Plan, VPP Policy)
// ============================================================================

function CustomerInfoSection({ customer }) {
  const { token } = theme.useToken();
  const [activeTab, setActiveTab] = useState("statements");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [vaultMasked, setVaultMasked] = useState(null);
  const [stmtPageSize, setStmtPageSize] = useState(10);
  const [vaultDecrypted, setVaultDecrypted] = useState(null);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultRevealed, setVaultRevealed] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankForm] = Form.useForm();
  const [statementMonth, setStatementMonth] = useState(dayjs());
  const [statements, setStatements] = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);

  // Fetch statements
  useEffect(() => {
    const fetchStatements = async () => {
      if (!customer.id) { setStatements([]); return; }
      setStatementsLoading(true);
      try {
        const startDate = statementMonth.startOf("month").format("YYYY-MM-DD");
        const endDate = statementMonth.endOf("month").format("YYYY-MM-DD");
        const response = await authenticatedFetch(
          `/api/settlements/ledger/customer?customerId=${customer.id}&startDate=${startDate}&endDate=${endDate}&limit=31`
        );
        const result = await response.json();
        if (result.r === 0 && result.data) {
          setStatements(result.data.map((entry) => ({
            date: entry.date,
            credit: parseFloat(entry.credit) || 0,
            debit: parseFloat(entry.debit) || 0,
            closingBalance: parseFloat(entry.closing_balance) || 0,
            revenue: parseFloat(entry.credit) || 0,
          })));
        } else {
          setStatements([]);
        }
      } catch (error) {
        console.error("Error fetching statements:", error);
        setStatements([]);
      } finally {
        setStatementsLoading(false);
      }
    };
    fetchStatements();
  }, [customer.id, statementMonth]);

  // Fetch vault (masked bank details) for customer
  useEffect(() => {
    if (!customer.id) return;
    const fetchVault = async () => {
      setVaultLoading(true);
      try {
        const resp = await authenticatedFetch(`/api/vault/customer/${customer.id}`);
        const result = await resp.json();
        if (result.r === 0 && result.d) {
          setVaultMasked(result.d);
        }
      } catch (err) {
        console.error("Error fetching vault:", err);
      } finally {
        setVaultLoading(false);
      }
    };
    fetchVault();
  }, [customer.id]);

  const handleRevealBank = async () => {
    if (!customer.id) return;
    setVaultLoading(true);
    try {
      const resp = await authenticatedFetch(`/api/vault/customer/${customer.id}/decrypt`);
      const result = await resp.json();
      if (result.r === 0 && result.d) {
        setVaultDecrypted(result.d);
        setVaultRevealed(true);
      } else {
        message.error("Failed to decrypt bank details");
      }
    } catch (err) {
      console.error("Error decrypting vault:", err);
      message.error("Failed to decrypt bank details");
    } finally {
      setVaultLoading(false);
    }
  };

  const handleSaveBank = async () => {
    if (!customer.id) {
      message.error("No customer linked to this system");
      return;
    }
    try {
      const values = await bankForm.validateFields();
      setSavingBank(true);
      const resp = await authenticatedFetch("/api/vault", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ownerType: "customer",
          ownerId: customer.id,
          bsb: values.bsb.replace(/[-\s]/g, ""),
          accountNumber: values.accountNumber.replace(/\s/g, ""),
          accountName: values.accountName || null,
          bankName: values.bankName || null,
        }),
      });
      const result = await resp.json();
      if (result.r === 0) {
        setVaultMasked({
          vaultId: result.d.vaultId,
          bankName: values.bankName || null,
          bsbLast2: result.d.bsbLast2,
          accountLast2: result.d.accountLast2,
          status: "active",
        });
        setVaultRevealed(false);
        setVaultDecrypted(null);
        setIsEditingBank(false);
        bankForm.resetFields();
        message.success("Bank details saved");
      } else {
        message.error(result.error || "Failed to save bank details");
      }
    } catch (error) {
      console.error("Error saving bank details:", error);
    } finally {
      setSavingBank(false);
    }
  };

  // Fetch available VPP plans and current customer plan
  useEffect(() => {
    if (!customer.id) return;
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const [plansResp, currentResp] = await Promise.all([
          authenticatedFetch("/api/customer/plans"),
          authenticatedFetch(`/api/customer/${customer.id}/plan`),
        ]);
        const plansResult = await plansResp.json();
        const currentResult = await currentResp.json();
        if (plansResult.r === 0 && plansResult.d) setAvailablePlans(plansResult.d);
        if (currentResult.r === 0 && currentResult.d) setCurrentPlan(currentResult.d);
      } catch (err) {
        console.error("Error fetching plans:", err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, [customer.id]);

  const handleChangePlan = async () => {
    if (!selectedPlan) return;
    if (!customer.id) {
      message.error("No customer linked to this system");
      return;
    }
    setIsChangingPlan(true);
    try {
      const resp = await authenticatedFetch(`/api/customer/${customer.id}/plan`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: selectedPlan }),
      });
      const result = await resp.json();
      if (result.r === 0 && result.d) {
        setCurrentPlan(result.d);
        setSelectedPlan(null);
        message.success(`Plan changed to ${result.d.name}`);
      } else {
        message.error(result.error || "Failed to change plan");
      }
    } catch (error) {
      console.error("Error changing plan:", error);
      message.error("Failed to change plan");
    } finally {
      setIsChangingPlan(false);
    }
  };

  const stmtTotals = statements.reduce(
    (acc, s) => ({ credit: acc.credit + s.credit, debit: acc.debit + s.debit, revenue: acc.revenue + s.revenue }),
    { credit: 0, debit: 0, revenue: 0 }
  );
  const stmtColumns = [
    { title: "Date", dataIndex: "date", key: "date", width: 100 },
    { title: "Credit", dataIndex: "credit", key: "credit", render: (v) => <Text style={{ color: token.colorSuccess }}>${v.toFixed(2)}</Text>, width: 80 },
    { title: "Debit", dataIndex: "debit", key: "debit", render: (v) => <Text style={{ color: token.colorError }}>${v.toFixed(2)}</Text>, width: 80 },
    { title: "Balance", dataIndex: "closingBalance", key: "closingBalance", render: (v) => <Text strong>${v.toFixed(2)}</Text>, width: 90 },
  ];

  const tabItems = [
    {
      key: "statements",
      label: <span><DollarOutlined style={{ marginRight: 4 }} />Statements</span>,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <DatePicker picker="month" value={statementMonth} onChange={setStatementMonth} allowClear={false} size="small" style={{ width: 140 }} />
          </div>
          <Table
            columns={stmtColumns}
            dataSource={statements}
            rowKey="date"
            size="small"
            loading={statementsLoading}
            pagination={makePagination(stmtPageSize, setStmtPageSize)}
            locale={{ emptyText: customer.id ? "No ledger entries for this month" : "No customer linked" }}
          />
          <div style={{ marginTop: 8, padding: 8, background: token.colorFillQuaternary, borderRadius: 8, display: "flex", justifyContent: "space-between" }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Total Credit</Text>
              <Text strong style={{ fontSize: 13, color: token.colorSuccess }}>${stmtTotals.credit.toFixed(2)}</Text>
            </div>
            <div style={{ textAlign: "center", flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Total Debit</Text>
              <Text strong style={{ fontSize: 13, color: token.colorError }}>${stmtTotals.debit.toFixed(2)}</Text>
            </div>
            <div style={{ textAlign: "center", flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 10, display: "block" }}>Net Revenue</Text>
              <Text strong style={{ fontSize: 13, color: token.colorSuccess }}>${stmtTotals.revenue.toFixed(2)}</Text>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "bank",
      label: "Bank Info",
      children: vaultLoading && !isEditingBank ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin size="small" /></div>
      ) : isEditingBank ? (
        <Form form={bankForm} layout="vertical" size="small">
          <Form.Item name="bankName" label="Bank Name">
            <Input placeholder="e.g. Commonwealth Bank" />
          </Form.Item>
          <Form.Item name="bsb" label="BSB" rules={[{ required: true, message: "BSB is required" }, { pattern: /^\d{3}-?\d{3}$/, message: "BSB must be 6 digits" }]}>
            <Input placeholder="e.g. 062-000" />
          </Form.Item>
          <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true, message: "Account number is required" }]}>
            <Input placeholder="e.g. 12345678" />
          </Form.Item>
          <Form.Item name="accountName" label="Account Name">
            <Input placeholder="e.g. John Smith" />
          </Form.Item>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button size="small" onClick={() => { setIsEditingBank(false); bankForm.resetFields(); }}>Cancel</Button>
              <Button size="small" type="primary" onClick={handleSaveBank} loading={savingBank}>Save</Button>
            </Space>
          </div>
        </Form>
      ) : vaultMasked ? (
        <div>
          <Descriptions column={1} size="small" labelStyle={{ width: 120 }}>
            <Descriptions.Item label="Bank Name">
              {vaultRevealed && vaultDecrypted ? vaultDecrypted.bankName : vaultMasked.bankName || <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="BSB">
              {vaultRevealed && vaultDecrypted
                ? <Text code>{vaultDecrypted.bsb}</Text>
                : <Text code>****{vaultMasked.bsbLast2}</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Account Number">
              {vaultRevealed && vaultDecrypted
                ? <Text code>{vaultDecrypted.accountNumber}</Text>
                : <Text code>******{vaultMasked.accountLast2}</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Account Name">
              {vaultRevealed && vaultDecrypted
                ? (vaultDecrypted.accountName || <Text type="secondary">-</Text>)
                : <Text type="secondary">Encrypted</Text>}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Space>
              {!vaultRevealed ? (
                <Button size="small" type="primary" ghost onClick={handleRevealBank} loading={vaultLoading}>
                  Reveal
                </Button>
              ) : (
                <Button size="small" onClick={() => { setVaultRevealed(false); setVaultDecrypted(null); }}>
                  Hide
                </Button>
              )}
              <Button size="small" icon={<EditOutlined />} onClick={() => setIsEditingBank(true)}>Edit</Button>
            </Space>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>No bank information available</Text>
          <Button size="small" type="primary" ghost onClick={() => setIsEditingBank(true)}>Add Bank Details</Button>
        </div>
      ),
    },
    // VPP Plan and VPP Policy tabs hidden for now (TODO: re-enable when ready)
    false && {
      key: "vppPlan",
      label: "VPP Plan",
      children: plansLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin size="small" /></div>
      ) : (
        <div>
          {currentPlan ? (
            <div style={{
              background: `linear-gradient(135deg, ${token.colorSuccess}15 0%, ${token.colorSuccess}05 100%)`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              border: `1px solid ${token.colorSuccess}30`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Current Plan</Text>
              </div>
              <Text strong style={{ fontSize: 16, display: "block", marginBottom: 8 }}>{currentPlan.name}</Text>
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Revenue Share</Text>
                  <div style={{ fontWeight: 600, color: token.colorSuccess }}>
                    {currentPlan.revenue_share_percent != null ? `${currentPlan.revenue_share_percent}%` : "-"}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Peak Bonus</Text>
                  <div style={{ fontWeight: 500, fontSize: 12 }}>
                    {currentPlan.peak_bonus_percent ? `+${currentPlan.peak_bonus_percent}% peak` : "None"}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Min Discharge</Text>
                  <div style={{ fontSize: 12 }}>
                    {currentPlan.min_discharge_kwh != null ? `${currentPlan.min_discharge_kwh} kWh/day` : "-"}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Max Discharge</Text>
                  <div style={{ fontSize: 12 }}>
                    {currentPlan.max_discharge_kwh != null ? `${currentPlan.max_discharge_kwh} kWh/day` : "Unlimited"}
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div style={{
              background: token.colorFillQuaternary,
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              textAlign: "center",
            }}>
              <Text type="secondary">No VPP plan assigned</Text>
            </div>
          )}

          {/* Change / Assign Plan */}
          <Divider style={{ margin: "12px 0", fontSize: 12 }}>
            <EditOutlined /> {currentPlan ? "Change Plan" : "Assign Plan"}
          </Divider>
          <div style={{ marginBottom: 12 }}>
            <Select
              placeholder={currentPlan ? "Select a new plan" : "Select a plan to assign"}
              style={{ width: "100%" }}
              value={selectedPlan}
              onChange={setSelectedPlan}
              options={availablePlans
                .filter(p => p.id !== currentPlan?.plan_id)
                .map(p => ({
                  value: p.id,
                  label: (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{p.name}</span>
                      <Tag color="green" style={{ marginRight: 0 }}>{p.revenue_share_percent}%</Tag>
                    </div>
                  ),
                }))}
            />
          </div>
          {selectedPlan && (
            <div style={{ background: token.colorFillQuaternary, padding: 12, borderRadius: 8, marginBottom: 12 }}>
              <Text style={{ fontSize: 12 }}>
                {availablePlans.find(p => p.id === selectedPlan)?.description}
              </Text>
            </div>
          )}
          <Button
            type="primary"
            block
            disabled={!selectedPlan}
            loading={isChangingPlan}
            onClick={handleChangePlan}
            style={{ borderRadius: 2 }}
          >
            {currentPlan ? "Confirm Plan Change" : "Assign Plan"}
          </Button>
        </div>
      ),
    },
    false && {
      key: "vppPolicy",
      label: (
        <span>
          <SafetyCertificateOutlined style={{ marginRight: 4 }} />
          VPP Policy
        </span>
      ),
      children: plansLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin size="small" /></div>
      ) : currentPlan && currentPlan.policy_json ? (
        <VppPolicyViewer policy={currentPlan.policy_json} policyId={currentPlan.policy_id} timezone={currentPlan.policy_timezone} />
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <SafetyCertificateOutlined style={{ fontSize: 32, color: token.colorTextQuaternary, display: "block", marginBottom: 12 }} />
          <Text type="secondary">No VPP policy assigned to this plan</Text>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>Account</Text>}
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" />
    </Card>
  );
}

// ============================================================================
// BOTTOM SECTION: Communication Timeline
// ============================================================================
function CommunicationSection({ systemId }) {
  const { token } = theme.useToken();
  const [messageText, setMessageText] = useState("");
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch communications from API
  const fetchCommunications = useCallback(async () => {
    if (!systemId) return;
    setLoading(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${systemId}/communications`);
      const result = await response.json();
      if (result.success && result.data) {
        const formatted = result.data.map((c) => ({
          id: c.id,
          type: c.type,
          from: c.fromName || (c.fromType === "customer" ? "Customer" : c.fromType === "powow" ? "Powow Support" : "Partner Support"),
          fromType: c.fromType,
          content: c.content,
          timestamp: dayjs(c.timestamp).format("YYYY-MM-DD HH:mm"),
        }));
        setCommunications(formatted);
      }
    } catch (error) {
      console.error("Error fetching communications:", error);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  const handleSend = async () => {
    if (!messageText.trim() || !systemId) return;

    setSending(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${systemId}/communications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "email",
          fromType: "partner",
          content: messageText,
        }),
      });
      const result = await response.json();
      if (result.success) {
        message.success("Message sent successfully");
        setMessageText("");
        fetchCommunications(); // Refresh the list
      } else {
        message.error(result.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending communication:", error);
      message.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getAvatarStyle = (fromType) => {
    switch (fromType) {
      case "powow":
        return { background: "linear-gradient(135deg, #54A2CC 0%, #3d8ab8 100%)" };
      case "partner":
        return { background: "linear-gradient(135deg, #05a677 0%, #048a63 100%)" };
      case "customer":
        return { background: "linear-gradient(135deg, #faad14 0%, #d48806 100%)" };
      default:
        return { background: token.colorPrimary };
    }
  };

  const getIcon = (type) => {
    return type === "call" ? <PhoneOutlined /> : <MailOutlined />;
  };

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={
        <Space>
          <CustomerServiceOutlined style={{ color: token.colorPrimary }} />
          <Text strong style={{ fontSize: 16 }}>Communication History</Text>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      {/* Message Input */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "stretch" }}>
        <TextArea
          placeholder="Type a message to send to the customer..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          rows={2}
          style={{ flex: 1, borderRadius: 2, resize: "none" }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          style={{ borderRadius: 2, height: "auto", minHeight: 54 }}
        >
          Send
        </Button>
      </div>

      {/* Timeline */}
      <div style={{ maxHeight: 300, overflowY: "auto", paddingRight: 8 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : communications.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: token.colorTextSecondary }}>
            No communication history
          </div>
        ) : (
          <Timeline
            items={communications.map((comm) => ({
              dot: (
                <Avatar size="small" style={getAvatarStyle(comm.fromType)} icon={getIcon(comm.type)} />
              ),
              children: (
                <div style={{ paddingBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <Space size={8}>
                      <Text strong style={{ fontSize: 13 }}>{comm.from}</Text>
                      <Tag color={comm.type === "call" ? "green" : comm.type === "sms" ? "orange" : "blue"} style={{ fontSize: 10 }}>
                        {comm.type === "call" ? "Call" : comm.type === "sms" ? "SMS" : "Email"}
                      </Tag>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 11 }}>{comm.timestamp}</Text>
                  </div>
                  <Paragraph style={{ margin: 0, fontSize: 13, color: token.colorTextSecondary }}>
                    {comm.content}
                  </Paragraph>
                </div>
              ),
            }))}
          />
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// INTERNAL NOTES SECTION
// ============================================================================
function InternalNotesSection({ systemId }) {
  const { token } = theme.useToken();
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Fetch notes from API
  const fetchNotes = useCallback(async () => {
    if (!systemId) return;
    setLoading(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${systemId}/notes`);
      const result = await response.json();
      if (result.success && result.data) {
        const formatted = result.data.map((n) => ({
          id: n.id,
          author: n.author || "Unknown",
          role: n.role || "User",
          content: n.content,
          timestamp: dayjs(n.timestamp).format("YYYY-MM-DD HH:mm"),
          priority: n.priority || "normal",
        }));
        setNotes(formatted);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async () => {
    if (!noteText.trim() || !systemId) return;

    setAdding(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${systemId}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: noteText,
          priority: "normal",
        }),
      });
      const result = await response.json();
      if (result.success) {
        message.success("Note added successfully");
        setNoteText("");
        fetchNotes(); // Refresh the list
      } else {
        message.error(result.message || "Failed to add note");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      message.error("Failed to add note");
    } finally {
      setAdding(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "linear-gradient(135deg, #54A2CC 0%, #3d8ab8 100%)",
      "linear-gradient(135deg, #05a677 0%, #048a63 100%)",
      "linear-gradient(135deg, #f5a623 0%, #d48806 100%)",
      "linear-gradient(135deg, #faad14 0%, #d48806 100%)",
      "linear-gradient(135deg, #eb2f96 0%, #c41d7f 100%)",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, height: "100%" }}
      title={
        <Space>
          <FormOutlined style={{ color: "#f5a623" }} />
          <Text strong style={{ fontSize: 16 }}>Internal Notes</Text>
        </Space>
      }
      styles={{
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 },
        body: { padding: 16 },
      }}
    >
      {/* Note Input */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "stretch" }}>
        <Input.TextArea
          placeholder="Add an internal note..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={2}
          style={{ flex: 1, borderRadius: 2, resize: "none" }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddNote}
          loading={adding}
          style={{ borderRadius: 2, height: "auto", minHeight: 54, background: "#f5a623", borderColor: "#f5a623" }}
        >
          Add
        </Button>
      </div>

      {/* Notes Timeline */}
      <div style={{ maxHeight: 300, overflowY: "auto", paddingRight: 8 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : notes.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: token.colorTextSecondary }}>
            No internal notes
          </div>
        ) : (
          <Timeline
            items={notes.map((note) => ({
              dot: (
                <Avatar size="small" style={{ background: getAvatarColor(note.author), fontSize: 10 }}>
                  {getInitials(note.author)}
                </Avatar>
              ),
              children: (
                <div style={{ paddingBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <Space size={8}>
                      <Text strong style={{ fontSize: 13 }}>{note.author}</Text>
                      <Tag color="purple" style={{ fontSize: 10 }}>{note.role}</Tag>
                      {note.priority === "high" && <Tag color="red" style={{ fontSize: 10 }}>High Priority</Tag>}
                      {note.priority === "urgent" && <Tag color="magenta" style={{ fontSize: 10 }}>Urgent</Tag>}
                      {note.priority === "low" && <Tag color="default" style={{ fontSize: 10 }}>Low</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 11 }}>{note.timestamp}</Text>
                  </div>
                  <Paragraph style={{ margin: 0, fontSize: 13, color: token.colorTextSecondary }}>
                    {note.content}
                  </Paragraph>
                </div>
              ),
            }))}
          />
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// REVISIONS SECTION
// ============================================================================
const FIELD_LABELS = {
  nmi:              "NMI",
  sn:               "Serial Number",
  street:           "Street",
  suburb:           "Suburb",
  state:            "State",
  postcode:         "Postcode",
  addressline:      "Address",
  customer_id:      "Customer Assignment",
  customer_name:    "Customer Name",
  customer_email:   "Customer Email",
  customer_phone:   "Customer Phone",
  customer_address: "Customer Address",
  customer_nmi:     "Customer NMI",
};

function RevisionsSection({ systemId }) {
  const { token } = theme.useToken();
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);

  const getInitials = (name) => {
    if (!name || name.startsWith("u0000")) return "S";
    return name[0].toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ["#f56a00", "#7265e6", "#ffbf00", "#00a2ae", "#1890ff", "#52c41a", "#eb2f96"];
    if (!name) return colors[0];
    const idx = name.charCodeAt(0) % colors.length;
    return colors[idx];
  };

  const formatFieldName = (field) => FIELD_LABELS[field] || field;

  const formatTimestamp = (ts) => {
    // Timestamp already converted to Sydney time by the backend
    return dayjs(ts).format("DD MMM YYYY, HH:mm");
  };

  const fetchRevisions = useCallback(async () => {
    if (!systemId) return;
    setLoading(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${systemId}/revisions`);
      const result = await response.json();
      if (result.success && result.data) {
        const formatted = result.data.map((r) => ({
          id:        r.id,
          fieldName: r.fieldName,
          oldValue:  r.oldValue ?? "—",
          newValue:  r.newValue ?? "—",
          author:    r.changedByName && !r.changedByName.startsWith("u0000") ? r.changedByName : "System",
          role:      r.changedByRole || r.changedByType || "system",
          timestamp: formatTimestamp(r.timestamp),
        }));
        setRevisions(formatted);
      }
    } catch (error) {
      console.error("Error fetching revisions:", error);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => {
    fetchRevisions();
  }, [fetchRevisions]);

  return (
    <Card
      title={
        <Space>
          <span>Revision History</span>
          <Tag color="blue">{revisions.length}</Tag>
        </Space>
      }
      size="small"
      style={{ marginTop: 16 }}
    >
      <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin size="small" />
          </div>
        ) : revisions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: token.colorTextSecondary, fontSize: 13 }}>
            No revisions recorded yet.
          </div>
        ) : (
          <Timeline
            style={{ paddingTop: 12 }}
            items={revisions.map((rev) => ({
              children: (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingBottom: 14, marginTop: -4 }}>
                  {/* Avatar circle — placed in children to avoid Timeline dot-container clipping */}
                  <div style={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: "50%",
                    background: getAvatarColor(rev.author),
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    userSelect: "none",
                  }}>
                    {getInitials(rev.author)}
                  </div>
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    {/* Row 1: name · role tag · field tag — timestamp */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <Space size={6} wrap>
                        <Typography.Text strong style={{ fontSize: 14 }}>{rev.author}</Typography.Text>
                        <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>{rev.role}</Tag>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>changed</Typography.Text>
                        <Tag color="geekblue" style={{ fontSize: 11, margin: 0 }}>{formatFieldName(rev.fieldName)}</Tag>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap", marginLeft: 8 }}>
                        {rev.timestamp} AEST
                      </Typography.Text>
                    </div>
                    {/* Row 2: old → new diff */}
                    <div style={{
                      background: token.colorFillAlter,
                      borderRadius: 6,
                      padding: "5px 12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                    }}>
                      <Typography.Text delete type="secondary" style={{ fontSize: 14 }}>{rev.oldValue}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 14 }}>→</Typography.Text>
                      <Typography.Text strong style={{ color: token.colorPrimary, fontSize: 14 }}>{rev.newValue}</Typography.Text>
                    </div>
                  </div>
                </div>
              ),
            }))}
          />
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Derive lifecycle status from system dates
const getLifecycleStatus = (system) => {
  if (system.offboard_date) {
    return "offboarded";
  }
  if (system.offboard_requested_date || system.offboard_approval_date || system.offboard_trial_date) {
    return "offboarding";
  }
  if (system.onboard_success_date) {
    return "onboarded";
  }
  if (system.onboard_date) {
    return "onboarding";
  }
  return "pending"; // No onboard_date set yet
};

// Get lifecycle status display properties
const getLifecycleStatusProps = (status) => {
  switch (status) {
    case "pending":
      return { color: "default", label: "Pending" };
    case "onboarding":
      return { color: "processing", label: "Onboarding" };
    case "onboarded":
      return { color: "success", label: "Onboarded" };
    case "offboarding":
      return { color: "warning", label: "Offboarding" };
    case "offboarded":
      return { color: "default", label: "Offboarded" };
    default:
      return { color: "default", label: status };
  }
};

// Get system status display properties (from np_system.status)
const getSystemStatusProps = (status) => {
  switch (status) {
    case "ACTIVE":
      return { color: "success", label: "Active" };
    case "INACTIVE":
      return { color: "default", label: "Inactive" };
    case "OFFBOARDED":
      return { color: "default", label: "Offboarded" };
    case "SUSPENDED":
      return { color: "error", label: "Suspended" };
    default:
      return { color: "default", label: status || "Unknown" };
  }
};

// ============================================================================
// MAIN SYSTEM DETAIL COMPONENT
// ============================================================================
export default function SystemDetail() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { systemSn } = useParams();
  const [loading, setLoading] = useState(true);

  const roleType = localStorage.getItem("roleType") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canViewRevisions   = isSuperadmin || userPermissions.includes("system:revisions_read");
  const canSubmitNmiChange = isSuperadmin || userPermissions.includes("approval:create");
  const canOffboard        = isSuperadmin || userPermissions.includes("system:offboard");
  const isProtectedView    = !isSuperadmin && userPermissions.includes("system:protected_view") && !userPermissions.includes("system:read");

  const [system, setSystem] = useState(null);
  const [offboardRequesting, setOffboardRequesting] = useState(false);
  const [nmiChangeModalOpen, setNmiChangeModalOpen] = useState(false);
  // Charts and Weather show one market (AEST) day, so default to the market's today, not the viewer's.
  const [selectedDate, setSelectedDate] = useState(marketToday());

  // Handle offboard request
  const handleOffboardRequest = async () => {
    if (!system?.id) {
      message.error("System ID not found");
      return;
    }

    setOffboardRequesting(true);
    try {
      const response = await authenticatedFetch(`/api/systems/${system.id}/offboard-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await response.json();

      if (result.success) {
        message.success("Offboard request submitted successfully");
        // Refresh system data
        setSystem((prev) => ({
          ...prev,
          offboardRequestedDate: result.data.offboardRequestedDate,
        }));
      } else {
        message.error(result.message || "Failed to submit offboard request");
      }
    } catch (error) {
      console.error("Error requesting offboard:", error);
      message.error("Failed to submit offboard request");
    } finally {
      setOffboardRequesting(false);
    }
  };

  useEffect(() => {
    const fetchSystem = async () => {
      setLoading(true);
      try {
        const response = await authenticatedFetch(`/api/systems/${systemSn}`);
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data;
          const lifecycleStatus = getLifecycleStatus(data);
          setSystem({
            id: data.id,
            sn: data.sn || systemSn,
            nmi: data.nmi || "",
            group: data.control_group_name || data.view_group_name || "Unassigned",
            groupId: data.control_group_id,
            connectionStatus: data.connection_status || data.online_status || "no_data",
            lastSeen: data.last_seen || null,
            // System status from np_system.status column
            systemStatus: data.status || "ACTIVE",
            // Lifecycle status derived from dates
            lifecycleStatus: lifecycleStatus,
            soc: null, // Populated below from realtime endpoint
            pvKw: data.pv_kw ? parseFloat(data.pv_kw) : null,
            inverterKw: data.inverter_kw ? parseFloat(data.inverter_kw) : null,
            batteryKwh: data.battery_kwh ? parseFloat(data.battery_kwh) : null,
            offboardRequestedDate: data.offboard_requested_date,
            offboardDate: data.offboard_date,
            // POW-187: the SITE address belongs to the system, not the customer.
            // np_customer holds one address but a customer can own several
            // systems, so falling back to it made every sibling system display
            // the same address — and editing it rewrote all of them.
            siteAddress: {
              street: data.street || "",
              suburb: data.suburb || "",
              state: data.state || "",
              postcode: data.postcode || "",
              addressline: data.addressline || "",
              longitude: data.longitude ?? null,
              latitude: data.latitude ?? null,
              // Composite for display. NEVER falls back to customer_address.
              display:
                data.addressline ||
                [data.street, data.suburb, data.state, data.postcode]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                "",
            },
            customer: {
              id: data.customer_id,
              name: data.customer_name || "",
              // The customer's CONTACT address — shared across their systems.
              // Kept deliberately separate from siteAddress above.
              address: data.customer_address || "",
              // How many systems this customer record is linked to. > 1 means
              // editing the customer card affects all of them.
              systemCount: Number(data.customer_system_count) || 1,
              phone: data.customer_phone || "",
              email: data.customer_email || "",
              totalRevenue: null, // TODO: Calculate from ledger
              monthRevenue: null, // TODO: Calculate from ledger
              bankInfo: data.customer_bank_info || null,
              vppPlan: data.vpp_plan || null,
            },
            systemConfig: {
              pvSize: data.pv_kw ? `${data.pv_kw} kW` : null,
              inverterModel: data.inverter_name || null,
              inverterSize: data.inverter_kw ? `${data.inverter_kw} kW` : null,
              batteryModel: data.battery_name || null,
              batterySize: data.battery_kwh ? `${data.battery_kwh} kWh` : null,
              controllerType: data.controller_type || null,
              controllerName: data.controller_name || null,
              meterModel: data.meter_model || null,
              // POW-190: np_system.onboard_date — a system property. Moved here
              // from the customer object, where it implied it belonged to the
              // shared customer record.
              onboardDate: data.onboard_date || "",
              extCrm: data.ext_crm || null,
              extCrmId: data.ext_crm_id || null,
            },
            viewGroups: (data.viewGroups || []).map((vg) => ({
              id: vg.id,
              name: vg.name,
              company: vg.ownerCompany || "—",
              systemCount: vg.systemCount || 0,
              joinedDate: vg.joinedAt ? dayjs(vg.joinedAt).format("YYYY-MM-DD") : "—",
            })),
            controlGroupStats: data.controlGroupStats || null,
            controlGroupRegion: data.control_group_region || null,
            controlGroupDnsp: data.control_group_dnsp || null,
            vppFcasStatus: data.vpp_fcas_status ?? null,
          });
          // Fetch SOC from Cassandra using the real SN (fire-and-forget)
          if (data.sn) {
            authenticatedFetch(`/api/systems/${data.sn}/realtime`)
              .then(r => r.json())
              .then(result => {
                if (result.success && result.data && result.data.soc != null) {
                  setSystem((prev) => prev ? { ...prev, soc: Math.round(result.data.soc) } : prev);
                }
              })
              .catch(err => console.warn("Failed to fetch SOC:", err.message));
          }
        } else {
          message.error(result.message || "System not found");
          setSystem(null);
        }
      } catch (error) {
        console.error("Error fetching system:", error);
        message.error("Failed to fetch system details");
        setSystem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSystem();
  }, [systemSn]);

  if (loading) {
    return (
      <PageLayout active="systemsSummary">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!system) {
    return (
      <PageLayout active="systemsSummary">
        <div style={{ textAlign: "center", padding: 48 }}>
          <Text type="secondary">System not found</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate("/systems/summary")}>
              Back to Systems
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="systemsSummary">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/systems/summary")}>Systems</a> },
          { title: "System Detail" },
          { title: system.sn },
        ]}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/systems/summary")}>
            Back
          </Button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Title level={2} style={{ marginBottom: 0 }}>{system.sn}</Title>
              {system.connectionStatus === "online" ? (
                <Tag color="success">Online</Tag>
              ) : system.connectionStatus === "no_data" ? (
                <Tooltip title="System has not reported to the VPP platform yet">
                  <Tag color="default">No Data</Tag>
                </Tooltip>
              ) : (
                <Tooltip title={system.lastSeen ? `Last seen: ${formatLocalWithZone(system.lastSeen)}` : "System is not reporting"}>
                  <Tag color="error">Offline</Tag>
                </Tooltip>
              )}
              {!isProtectedView && (
                <>
                  {system.vppFcasStatus !== null && system.vppFcasStatus !== undefined ? (
                    <Tag color={VPP_FCAS_COLORS[system.vppFcasStatus] ?? "default"}>
                      {VPP_FCAS_LABELS[system.vppFcasStatus] ?? `VPP ${system.vppFcasStatus}`}
                    </Tag>
                  ) : (
                    <Tag color="default">VPP N/A</Tag>
                  )}
                </>
              )}
            </div>
            <Text type="secondary">{isProtectedView ? `Group: ${system.group}` : `NMI: ${system.nmi} | Group: ${system.group}`}</Text>
          </div>
        </div>

        {/* Quick Stats and Actions */}
        <Space size={16}>
          <Statistic
            title="SOC"
            value={system.soc != null ? system.soc : (system.connectionStatus === "no_data" ? "N/A" : "-")}
            suffix={system.soc != null ? "%" : ""}
            valueStyle={{ fontSize: 18, color: system.soc != null ? (system.soc < 20 ? token.colorError : token.colorSuccess) : token.colorTextSecondary }}
          />
          <Statistic
            title="Inverter"
            value={system.inverterKw != null ? system.inverterKw : "-"}
            suffix={system.inverterKw != null ? "kW" : ""}
            valueStyle={{ fontSize: 18, color: system.inverterKw != null ? token.colorText : token.colorTextSecondary }}
            prefix={system.inverterKw != null ? <ThunderboltOutlined style={{ fontSize: 14, color: token.colorWarning }} /> : null}
          />
          <Statistic
            title="Battery"
            value={system.batteryKwh != null ? system.batteryKwh : "-"}
            suffix={system.batteryKwh != null ? "kWh" : ""}
            valueStyle={{ fontSize: 18, color: system.batteryKwh != null ? token.colorText : token.colorTextSecondary }}
            prefix={system.batteryKwh != null ? <BankOutlined style={{ fontSize: 14, color: "#f5a623" }} /> : null}
          />
          {!isProtectedView && (
            <div style={{ borderLeft: `1px solid ${token.colorBorderSecondary}`, paddingLeft: 24 }}>
              <Space>
                {canSubmitNmiChange && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setNmiChangeModalOpen(true)}
                    style={{ borderRadius: 2 }}
                  >
                    NMI / SN Change
                  </Button>
                )}
                {canOffboard && (
                  system.offboardRequestedDate ? (
                    <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ padding: "4px 12px" }}>
                      Offboard Requested
                    </Tag>
                  ) : system.offboardDate ? (
                    <Tag color="default" icon={<CheckCircleOutlined />} style={{ padding: "4px 12px" }}>
                      Offboarded
                    </Tag>
                  ) : (
                    <Popconfirm
                      title="Request Offboard"
                      description="Are you sure you want to request offboard for this system?"
                      onConfirm={handleOffboardRequest}
                      okText="Yes, Request Offboard"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        icon={<LogoutOutlined />}
                        loading={offboardRequesting}
                        style={{ borderRadius: 2 }}
                      >
                        Request Offboard
                      </Button>
                    </Popconfirm>
                  )
                )}
              </Space>
            </div>
          )}
        </Space>
      </div>

      {/* Customer Details (compact, top) */}
      {isProtectedView ? (
        <Card
          bordered={false}
          style={{ borderRadius: 12, marginBottom: 16 }}
          styles={{ body: { padding: "12px 16px" } }}
        >
          <Space>
            <UserOutlined style={{ color: token.colorPrimary }} />
            <Text strong style={{ fontSize: 16 }}>Customer:</Text>
            <Text>{system.customer?.name ? system.customer.name.split(" ")[0] : "-"}</Text>
          </Space>
        </Card>
      ) : (
        <>
        {/* POW-187: the system's own site address, above the shared customer
            record so the distinction is obvious at a glance. */}
        <SiteAddressCard
          systemId={system.id}
          siteAddress={system.siteAddress}
          onSiteAddressUpdated={(updated) => {
            setSystem((prev) => ({
              ...prev,
              siteAddress: {
                ...prev.siteAddress,
                street: updated.street || "",
                suburb: updated.suburb || "",
                state: updated.state || "",
                postcode: updated.postcode || "",
                addressline: updated.addressline || "",
                latitude: updated.latitude ?? null,
                longitude: updated.longitude ?? null,
                display:
                  updated.addressline ||
                  [updated.street, updated.suburb, updated.state, updated.postcode]
                    .filter(Boolean).join(" ").trim() || "",
              },
            }));
          }}
        />
        <CustomerDetailsCompact
          customer={system.customer}
          onCustomerUpdated={(updated) => {
            setSystem((prev) => ({
              ...prev,
              customer: {
                ...prev.customer,
                name: updated.name || prev.customer.name,
                email: updated.email || prev.customer.email,
                phone: updated.phone || prev.customer.phone,
                address: updated.address || prev.customer.address,
              },
            }));
          }}
        />
        </>
      )}

      {/* Main Content: System Details (60%) + Account (40%) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={isProtectedView ? 24 : 14}>
          <SystemDataSection
            systemSn={system.sn}
            systemConfig={system.systemConfig}
            groupName={system.group}
            groupId={system.groupId}
            viewGroups={system.viewGroups}
            controlGroupStats={system.controlGroupStats}
            controlGroupRegion={system.controlGroupRegion}
            controlGroupDnsp={system.controlGroupDnsp}
            selectedDay={selectedDate}
            onDayChange={setSelectedDate}
          />
        </Col>
        {!isProtectedView && (
          <Col xs={24} lg={10}>
            <CustomerInfoSection customer={system.customer} />
          </Col>
        )}
      </Row>

      {/* Communication History + Internal Notes — hidden for now (TODO: re-enable when backend ready) */}
      {/*
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <CommunicationSection systemId={system.id} />
        </Col>
        <Col xs={24} lg={10}>
          <InternalNotesSection systemId={system.id} />
        </Col>
      </Row>
      */}

      {/* Revision History — only visible to users with system:revisions_read */}
      {canViewRevisions && <RevisionsSection systemId={system.id} />}

      {/* NMI / SN Change Modal — pre-filled with this system, no selector shown */}
      <NmiChangeModal
        open={nmiChangeModalOpen}
        onClose={() => setNmiChangeModalOpen(false)}
        onSuccess={() => {}}
        prefilledSystem={{
          id: system.id,
          sn: system.sn,
          nmi: system.nmi,
          controller_name: system.systemConfig?.controllerName,
        }}
      />
    </PageLayout>
  );
}
